import React, { useEffect, useState } from 'react';
import { Icon } from '../../../components/Component';
import { motion } from 'framer-motion';

const CardTabs = ({ cardTabs, activeCardTab, setActiveCardTab, dataFetched }) => {
    const [tabs, setTabs] = useState(cardTabs);
    const [activeTab, setActiveTab] = useState(activeCardTab);
    const [isMobile, setIsMobile] = useState(false);
    const [showLeftFade, setShowLeftFade] = useState(false);
    const [showRightFade, setShowRightFade] = useState(true);

    useEffect(() => {
        const checkIfMobile = () => setIsMobile(window.innerWidth <= 768);
        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    const handleScroll = (e) => {
        const { scrollLeft, scrollWidth, clientWidth } = e.target;
        setShowLeftFade(scrollLeft > 0);
        setShowRightFade(scrollLeft < scrollWidth - clientWidth);
    };

    // Animation variants (same as before)
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const tabVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: "spring",
                stiffness: 300,
                damping: 20
            }
        }
    };

    const tabHoverEffects = isMobile ? {} : {
        hover: {
            scale: 1.03,
            transition: { type: "spring", stiffness: 400, damping: 10 }
        },
        tap: {
            scale: 0.98
        }
    };

    const activeTabEffects = {
        active: {
            scale: isMobile ? 1 : 1.05,
            transition: { type: "spring", stiffness: 400 }
        },
        inactive: {
            scale: 1,
            transition: { duration: 0.1 }
        }
    };

    useEffect(() => {
        setTabs(cardTabs);
        setActiveTab(activeCardTab);
    }, [activeCardTab, cardTabs]);

    return (
        <div style={{ position: 'relative', width: '100%', paddingLeft: 4, paddingRight: 4 }}>
            {/* Left fade indicator */}
            {isMobile && showLeftFade && (
                <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '20px',
                    background: 'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
                    zIndex: 1,
                    pointerEvents: 'none'
                }} />
            )}

            {/* Right fade indicator */}
            {isMobile && showRightFade && (
                <div style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '20px',
                    background: 'linear-gradient(270deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
                    zIndex: 1,
                    pointerEvents: 'none'
                }} />
            )}

            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                style={{
                    position: 'relative',
                    width: '100%',
                    overflowX: isMobile ? 'auto' : 'visible',
                    WebkitOverflowScrolling: 'touch',
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    paddingBottom: isMobile ? '10px' : '0' // Space for scroll hint
                }}
                onScroll={isMobile ? handleScroll : undefined}
            >
                <motion.ul
                    style={{
                        display: 'flex',
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        position: 'relative',
                        borderBottom: '1px solid #dee2e6',
                        gap: isMobile ? '4px' : '8px',
                        minWidth: isMobile ? 'max-content' : 'auto',
                        paddingBottom: '1px'
                    }}
                >
                    {tabs?.map((item, index) => (
                        <motion.li
                            key={`tab-${index}`}
                            variants={tabVariants}
                            style={{
                                marginRight: isMobile ? '2px' : '4px',
                                flexShrink: 0
                            }}
                        >
                            <motion.a
                                href="#"
                                onClick={(ev) => {
                                    ev.preventDefault();
                                    setActiveCardTab(item?.tabName);
                                }}
                                variants={activeTab === item?.tabName ? activeTabEffects : tabHoverEffects}
                                animate={activeTab === item?.tabName ? "active" : "inactive"}
                                whileHover={isMobile ? {} : "hover"}
                                whileTap="tap"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    padding: isMobile ? '8px 12px' : '12px 16px',
                                    color: activeTab === item?.tabName ? '#6576ff' : '#526484',
                                    textDecoration: 'none',
                                    position: 'relative',
                                    backgroundColor: 'transparent',
                                    cursor: 'pointer',
                                    fontSize: isMobile ? '13px' : '14px',
                                    fontWeight: activeTab === item?.tabName ? '500' : '400',
                                    border: 'none',
                                    outline: 'none',
                                    whiteSpace: 'nowrap',
                                    minWidth: 'max-content'
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: isMobile ? '4px' : '8px'
                                }}>
                                    {item?.custom_icon ? (
                                        <motion.span
                                            whileHover={isMobile ? {} : { scale: 1.1 }}
                                            whileTap={{ scale: 0.95 }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            {item?.custom_icon}
                                        </motion.span>
                                    ) : (
                                        <motion.span
                                            whileHover={isMobile ? {} : { scale: 1.1 }}
                                            whileTap={{ scale: 0.95 }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <Icon name={item?.icon} />
                                        </motion.span>
                                    )}
                                    <motion.span
                                        whileHover={isMobile ? {} : { scale: 1.05 }}
                                        style={{
                                            display: 'inline-block',
                                            flexShrink: 0
                                        }}
                                    >
                                        {item?.tabName}
                                    </motion.span>
                                </div>

                                {activeTab === item?.tabName && (
                                    <motion.div
                                        layoutId="activeIndicator"
                                        initial={false}
                                        transition={{
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 30
                                        }}
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '2px',
                                            backgroundColor: '#6576ff',
                                            borderRadius: '2px 2px 0 0'
                                        }}
                                    />
                                )}
                            </motion.a>
                        </motion.li>
                    ))}
                </motion.ul>
            </motion.div>

            {/* Mobile scroll hint (chevrons) */}
            {isMobile && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '4px'
                }}>
                    <Icon name="chevron-left" size="sm" className="text-light" />
                    <span className="small" style={{ color: '#999' }}>Swipe to see more</span>
                    <Icon name="chevron-right" size="sm" className="text-light" />
                </div>
            )}

            <style>{`
                [style*="overflow-x: auto"]::-webkit-scrollbar {
                    display: none;
                }
            `}</style>
        </div>
    );
};

export default CardTabs;